import {createColumnHelper} from '@tanstack/react-table';
import {
  DeleteButtonWithConfirmation,
  ShowButton,
  DocumentButton,
} from '@/components/refinedev/action_buttons';
import {defaultQueryOptions} from '../../_utils';
import StateBoundary from '@/components/basic/StateBoundary';
import {EditButtonWorkflow} from './components/buttons';
import useRefineTanstackDataTable from '@/hooks/refinedev/useRefineTanstackDataTable';
import {DataTable} from '@/components/data-table/data-table';
import DefaultDataTableToolbar from '@/components/refinedev/data-table/DefaultDataTableToolbar';
import {DataTableColumnHeader} from '@/components/data-table/data-table-column-header';
import {CreateProjectDialogButton} from './components/project-create-dialog-button';
import {ProjectEditDialogButton} from './components/project-edit-dialog-button';
import ProjectFilters from './components/project-filter';
import formatter from '@/lib/formatter';
import {TaskStatuses} from '../tasks/types';
import {useSearchParams} from 'react-router-dom';
import {CrudFilter} from '@refinedev/core';
import {useEffect} from 'react';

const thisResource = 'projects';

const columnHelper = createColumnHelper<any>();

const columns = [
  columnHelper.accessor('id', {
    header: ({column}) => <DataTableColumnHeader column={column} title="ID" />,
    cell: info => info.getValue(),
  }),
  columnHelper.accessor('name', {
    header: ({column}) => <DataTableColumnHeader column={column} title="Tên dự án" />,
    cell: info => info.renderValue(),
  }),
  columnHelper.accessor('location', {
    header: ({column}) => <DataTableColumnHeader column={column} title="Địa điểm" />,
    cell: info => info.renderValue(),
  }),
  columnHelper.accessor('start_date', {
    meta: {label: 'Bắt đầu'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => formatter.formatDate(info.getValue()),
  }),
  columnHelper.accessor('expected_end_date', {
    meta: {label: 'Kết thúc (dự kiến)'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => formatter.formatDate(info.getValue()),
  }),
  columnHelper.accessor('investment', {
    meta: {label: 'Số tiền đầu tư'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => info.renderValue(),
  }),
  columnHelper.accessor('status', {
    meta: {label: 'Trạng thái'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => TaskStatuses.find(v => v.id === info.getValue())?.label || '',
    enableSorting: false,
  }),
  columnHelper.display({
    id: 'actions',
    header: ({column}) => <DataTableColumnHeader column={column} title="Hành động" />,
    cell: ({row}) => (
      <div className="flex flex-row items-center">
        <ShowButton
          id={String(row.original.id)}
          resource={thisResource}
          canParams={{my_role: row.original.my_role}}
          // forceCan={row.original.my_role}
        />
        <ProjectEditDialogButton
          id={String(row.original.id)}
          canParams={{my_role: row.original.my_role}}
          // forceCan={row.original.my_role ? row.original.my_role === 'manager' : undefined}
        />
        <EditButtonWorkflow
          id={String(row.original.id)}
          resource={thisResource}
          canParams={{my_role: row.original.my_role}}
          // forceCan={row.original.my_role ? row.original.my_role === 'manager' : undefined}
        />
        <DocumentButton id={String(row.original.id)} canParams={{my_role: row.original.my_role}} />
        <DeleteButtonWithConfirmation
          id={String(row.original.id)}
          resource={thisResource}
          canParams={{my_role: row.original.my_role}}
        />
      </div>
    ),
  }),
];

const ProjectList = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const {refineTable, tanstackTable} = useRefineTanstackDataTable(
    {
      resource: thisResource,
      sorters: {initial: [{field: 'id', order: 'asc'}]},

      filters: {
        initial: searchParams.get('status')
          ? [
              {
                field: 'status',
                operator: 'eq',
                value: searchParams.get('status'),
              },
            ]
          : [],
      },
      queryOptions: defaultQueryOptions,
      syncWithLocation: true,
    },
    {columns},
  );

  useEffect(() => {
    const status = searchParams.get('status');
    if (status) {
      searchParams.delete('status');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  return (
    <StateBoundary
      isLoading={refineTable.tableQuery.isLoading}
      error={refineTable.tableQuery.error}>
      <div className="p-2">
        <DataTable
          table={tanstackTable}
          toolbar={
            <DefaultDataTableToolbar
              resource={thisResource}
              table={tanstackTable}
              createButton={
                <CreateProjectDialogButton onSuccess={() => refineTable.tableQuery.refetch()} />
              }
              filters={<ProjectFilters table={refineTable} />}
            />
          }
        />
      </div>
    </StateBoundary>
  );
};

ProjectList.table_columns = columns;

export default ProjectList;
